"use client";

import { NotLoggedIn } from "@components/ui/not-logged-in/NotLoggedIn";
import { MyAccountSkeleton } from "@components/ui/skeletons/my-account-skeleton";
import { useAuth } from "@lib/auth/useAuth";
import { useGetMyProfile } from "@store/hooks";
import { TwoColumnsPage } from "@templates/TwoColumnPage";
import { Box } from "@ecco/ui/box";
import styles from "@components/ui/ecco-club/faqSection.module.css";
import JoinEccoClub from "@components/ui/ecco-club/cards/JoinEccoClub";
import MemberBenefits from "@components/ui/ecco-club/cards/MemberOnlyBenefits";
import { Ribbon } from "@components/ui/ecco-club/ribbon";
import { isMarketWithLoyalty, hasPointsAndTiers } from "@lib/utils/loyalty";
import type { AccountPageProps } from "@templates/AccountPage";
import MembershipCard from "@components/ui/ecco-club/cards/MembershipCard";
import EccoPassCard from "@components/ui/ecco-club/cards/EccoPassCard";
import { useGlobalContent } from "@app/[locale]/GlobalContentProvider";
import { GradientBorder } from "@components/ui/ecco-club/GradientBorder";
import dynamic from "next/dynamic";

const BenefitsList = dynamic(
    () =>
        import("@components/auth/sign-up/components/benefits-list").then((mod) => mod.BenefitsList),
    { ssr: false }
);

type EccoClubClientProps = Pick<AccountPageProps, "page">;

export default function EccoClubClient({ page }: EccoClubClientProps) {
    const { data: customer, isLoading: isLoadingProfile } = useGetMyProfile();
    const { isLoggedIn, isLoading } = useAuth();
    const { siteSettings } = useGlobalContent();
    const eccoClubBenefits = siteSettings.auth.eccoClubBenefits;

    const firstName = customer?.firstName || "";
    const lastName = customer?.lastName || "";
    const navigationTitle = `${firstName} ${lastName}`.trim();

    if (isLoading || isLoadingProfile) {
        return <MyAccountSkeleton />;
    }

    if (!isLoggedIn) {
        return (
            <>
                <div hidden data-testid="myAccountProfile" />
                <NotLoggedIn />
            </>
        );
    }

    const EccoClubPrefixContent = customer?.loyalty ? (
        <MembershipCard
            tierLevel={customer.loyalty?.tier || ""}
            pointsBalance={customer.loyalty?.pointBalance || 0}
            membershipId={customer.loyalty?.cardNumber || ""}
        />
    ) : (
        <JoinEccoClub />
    );

    const EccoClubSuffixContent = customer?.loyalty && (
        <>
            {eccoClubBenefits?.length > 0 && (
                <Box bg="white" p={[3, 3, 5]} mb={0.5}>
                    <GradientBorder gap={[4, 2]}>
                        <BenefitsList benefits={eccoClubBenefits} />
                    </GradientBorder>
                </Box>
            )}
            <Box
                display={{ base: "none", lg: "flex" }}
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                p={8}
                bg="white"
            >
                <Box width="100%" bg="gray.100" p="50px">
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        flexDirection="row"
                        width="40%"
                        mx="auto"
                    >
                        <EccoPassCard />
                    </Box>
                </Box>
            </Box>
        </>
    );

    return (
        <>
            <div hidden data-testid="myAccountEccoClub" />
            <Box className={styles.faqWrapper}>
                <TwoColumnsPage
                    navigationTitle={navigationTitle}
                    showNavigation={false}
                    page={page}
                    leftColumn={{
                        prefix: EccoClubPrefixContent,
                        suffix: EccoClubSuffixContent,
                    }}
                />
            </Box>
            {hasPointsAndTiers && (
                <Box bg="gray.50" paddingBottom={[8, 12]}>
                    <MemberBenefits />
                </Box>
            )}
            {isMarketWithLoyalty && (
                <Box
                    css={{
                        width: "100%",
                        height: "9",
                        position: "relative",
                    }}
                >
                    <Ribbon />
                </Box>
            )}
        </>
    );
}
